import { asset, optimized } from '../lib/assets';
import type { GalleryItem, MediaAsset, Project, Source } from '../types/content';

export const suppliedSource: Source = {
  label: 'Material fornecido pela assessoria',
  pendingValidation: true,
};

function media(
  original: string,
  slug: string | null,
  widths: number[],
  dimensions: [number, number],
  alt: string,
  caption?: string,
): MediaAsset {
  return {
    original: asset(`images/${original}`),
    variants: slug
      ? widths.flatMap((width) => {
          const variants: MediaAsset['variants'] = [{ width, format: 'webp', src: optimized(slug, width) }];
          if (
            (slug === 'traiano-main' && width === 800) ||
            (slug === 'centro-eventos' && width === 1280) ||
            (slug === 'asfalto-ruas' && width === 1920) ||
            slug === 'palmas-scene' ||
            slug === 'parceria-background2'
          ) {
            variants.unshift({ width, format: 'avif', src: optimized(slug, width, 'avif') });
          }
          return variants;
        })
      : [],
    width: dimensions[0],
    height: dimensions[1],
    alt,
    caption,
    editingType: 'unknown',
    authorizationNote: 'Em validação',
  };
}

export const portrait = media(
  'traianoalone3.png',
  'traiano-alone3',
  [800, 1280],
  [1487, 2707],
  'Ademar Traiano sentado, vestindo camisa azul clara',
);

export const heroScene = media(
  'palmasprscene.png',
  'palmas-scene',
  [800, 1280, 1600],
  [1672, 941],
  'Vista panorâmica de Palmas, Paraná',
);

export const partnershipBackground = media(
  'backgroundparceria2.png',
  'parceria-background2',
  [800, 1280, 1600],
  [1672, 941],
  'Palmas e bandeira do Paraná com a mensagem Juntos por um Paraná melhor',
);

export const partnershipSlogan = media(
  'fixedversionslogan.png',
  'parceria-slogan',
  [320, 640],
  [1254, 1254],
  'Juntos por um Paraná melhor',
);

export const advancingSlogan = media(
  'palmassegueavancando.png',
  'palmas-avancando',
  [640, 1280, 1920],
  [2172, 724],
  'Palmas segue avançando',
);

const projectRecords: Project[] = [
  {
    slug: 'asfalto-pedras',
    area: 'infraestrutura',
    title: 'Asfalto sobre pedras irregulares',
    summary: 'Investimento para pavimentar 60 ruas e ampliar segurança e mobilidade nos bairros.',
    value: 14_885_912.74,
    valueLabel: 'R$ 14.885.912,74',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: '60 ruas',
    quantities: { streets: 60 },
    media: [media('AsfaltoSobPedrasIrregulares1.png', 'asfalto-ruas', [800, 1280, 1920], [2222, 1667], 'Obra de pavimentação sobre pedras irregulares em Palmas')],
    sources: [suppliedSource],
    featured: true,
  },
  {
    slug: 'asfalto-natural',
    area: 'infraestrutura',
    title: 'Asfalto sobre leito natural',
    summary: 'Novo convênio para pavimentar 32 ruas de terra.',
    value: 12_000_000,
    valueLabel: 'R$ 12 milhões',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: '32 ruas',
    quantities: { streets: 32 },
    media: [
      media('RuaAlceuFerreiraDeSiqueiraAntes.png', 'rua-alceu-antes', [800, 1280, 1920], [2222, 1250], 'Rua Alceu Ferreira de Siqueira antes da pavimentação'),
      media('RuaAlceuFerreiraDeSiqueiraDepois.png', 'rua-alceu-depois', [800, 1280, 1920], [2259, 1271], 'Rua Alceu Ferreira de Siqueira após a pavimentação'),
    ],
    sources: [suppliedSource],
  },
  {
    slug: 'asfalto-rural',
    area: 'infraestrutura',
    title: 'Asfalto rural — Estrada Fundo Grande',
    summary: 'Pavimentação da Estrada Fundo Grande.',
    value: 15_999_575.35,
    valueLabel: 'R$ 15.999.575,35',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: '9.950 metros',
    quantities: { roadLengthMeters: 9950 },
    media: [
      media('EstradaFundoGrandeAntes.png', 'fundo-grande-antes', [800, 1280], [1778, 1000], 'Estrada Fundo Grande antes do asfalto'),
      media('EstradaFundoGrandeDepois.png', 'fundo-grande-depois', [800, 1280, 1920], [2222, 1250], 'Estrada Fundo Grande durante a pavimentação'),
    ],
    sources: [suppliedSource],
  },
  {
    slug: 'maquinas-e-equipamentos',
    area: 'infraestrutura',
    title: 'Máquinas e equipamentos',
    summary: 'Reforço de maquinário apresentado para manutenção de estradas, obras e serviços no interior do município.',
    value: 3_700_000,
    valueLabel: 'R$ 3,7 milhões',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: '2 retroescavadeiras, 1 motoniveladora, 1 rolo compactador e 2 caminhões',
    quantities: { machines: 6 },
    media: [
      media('Retroescavadeiras.png', 'retroescavadeiras', [800, 1280], [1600, 1200], 'Retroescavadeiras apresentadas entre os equipamentos destinados a Palmas'),
      media('maquinas-extra-1.png', 'maquinas-extra-1', [800, 1280], [1600, 1200], 'Caminhão basculante branco ao lado de um micro-ônibus'),
      media('maquinas-extra-2.png', 'maquinas-extra-2', [800, 1280], [1600, 1200], 'Caminhão basculante vermelho'),
    ],
    sources: [suppliedSource],
  },
  {
    slug: 'ubs',
    area: 'saude',
    title: 'Duas futuras Unidades Básicas de Saúde',
    summary: 'Recursos para ampliar a estrutura de atenção básica em Palmas.',
    value: 2_000_000,
    valueLabel: 'R$ 2 milhões',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: '2 unidades',
    quantities: { units: 2 },
    media: [media('UBSprototipo.png', 'ubs-prototipo', [800], [1064, 639], 'Representação de uma Unidade Básica de Saúde')],
    sources: [suppliedSource],
    featured: true,
  },
  {
    slug: 'veiculos-saude',
    area: 'saude',
    title: 'Veículos para servir a população',
    summary: 'Ônibus, ambulâncias, veículos de sete lugares e vans apresentados para reforçar o atendimento.',
    value: 2_840_000,
    valueLabel: 'R$ 2,84 milhões',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: '10 veículos: 3 vans, 2 ambulâncias, 2 ônibus e 3 veículos de sete lugares',
    quantities: { vehicles: 10 },
    media: [
      media('01VAN.png', 'van-saude', [800, 1280], [1448, 1086], 'Uma van apresentada para o atendimento em saúde em Palmas'),
      media('02Vans.png', 'vans-saude', [800, 1280], [1280, 960], 'Duas vans apresentadas para o atendimento em saúde em Palmas'),
      media('02Ambulancias.png', 'ambulancias-saude', [800, 1280], [1600, 1200], 'Duas ambulâncias apresentadas para a saúde em Palmas'),
      media('02Onibus.png', 'onibus-saude', [800, 1280], [1600, 1200], 'Dois ônibus apresentados para o atendimento em saúde em Palmas'),
      media('03Veiculosde7Lugares.png', 'veiculos-sete-lugares', [800, 1280], [1280, 960], 'Três veículos de sete lugares apresentados para a saúde em Palmas'),
    ],
    sources: [suppliedSource],
  },
  {
    slug: 'centro-eventos',
    area: 'grandes-obras',
    title: 'Centro de Eventos Maria José Piana',
    summary: 'Novo espaço para eventos, feiras, encontros e atividades culturais.',
    value: 17_500_000,
    valueLabel: 'R$ 17,5 milhões',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: 'Cultura, turismo e negócios',
    media: [media('CentroDeEventosMariaJosePianaCima.png', 'centro-eventos', [800, 1280], [1778, 1000], 'Vista superior do Centro de Eventos Maria José Piana')],
    sources: [suppliedSource],
    featured: true,
  },
  {
    slug: 'terminal',
    area: 'grandes-obras',
    title: 'Reforma do Terminal Rodoviário',
    summary: 'Modernização com acessibilidade e requalificação dos espaços de atendimento.',
    value: 3_500_000,
    valueLabel: 'R$ 3,5 milhões',
    fundingType: 'publico',
    status: 'em_validacao',
    scope: 'Modernização e acessibilidade',
    media: [media('ReformaDoTerminalRodoviario.png', 'terminal-rodoviario', [800, 1280, 1920], [2222, 1667], 'Terminal Rodoviário de Palmas')],
    sources: [suppliedSource],
  },
  {
    slug: 'portais-de-entrada',
    area: 'grandes-obras',
    title: 'Portais de entrada',
    summary: 'Novos portais apresentados para reforçar a identidade da cidade e a recepção de visitantes.',
    value: 705_885.15,
    valueLabel: 'R$ 705.885,15',
    fundingType: 'contrapartida',
    status: 'em_validacao',
    scope: 'Entradas da cidade',
    media: [media('PalmasFachada.png', 'palmas-fachada', [800, 1280, 1920], [2222, 1250], 'Portal de entrada apresentado para Palmas')],
    sources: [suppliedSource],
  },
  {
    slug: 'eolico',
    area: 'investimento-privado',
    title: 'Complexo Eólico Palmas II',
    summary: 'Empreendimento privado apresentado separadamente dos recursos públicos.',
    value: 3_500_000_000,
    valueLabel: 'R$ 3,5 bilhões',
    fundingType: 'privado',
    status: 'em_validacao',
    privateDetails: {
      company: {},
      capacity: { value: '504 MW', source: suppliedSource },
      turbines: { value: '72', source: suppliedSource },
      jobs: { value: 'Até 5 mil', source: suppliedSource },
    },
    media: [media('ComplexoEolicoPalmasII.png', 'complexo-eolico', [800, 1280], [1836, 1033], 'Complexo Eólico Palmas II')],
    sources: [suppliedSource],
  },
];

export const projects: Project[] = projectRecords.map((project) => ({
  valueSource: suppliedSource,
  location: 'Palmas, Paraná',
  deputyParticipation: 'em_validacao',
  category: ['maquinas-e-equipamentos', 'veiculos-saude'].includes(project.slug)
    ? 'equipamento' : ['ubs', 'terminal'].includes(project.slug) ? 'projeto_futuro' : 'obra_projeto',
  valueType: project.fundingType === 'privado' ? 'investimento_privado'
    : project.fundingType === 'publico' ? 'recurso_publico' : 'em_validacao',
  ...project,
  scope: project.privateDetails ? `${project.privateDetails.turbines?.value || 'Dado a confirmar'} aerogeradores · ${project.privateDetails.capacity?.value || 'Dado a confirmar'}` : project.scope,
  media: project.media.map((item) => ({ ...item, relatedProject: project.slug, location: 'Palmas, Paraná' })),
}));

export const publicHeadline = { value: 75_000_000, label: 'R$ 75 MILHÕES', pendingValidation: true };

export const testimonial = {
  name: 'Daniel Langaro', role: 'Prefeito de Palmas',
  text: 'O Deputado Traiano sempre esteve ao nosso lado, ouvindo nossas demandas e ajudando a abrir portas junto ao Governo do Estado. Graças a essa parceria, conquistamos investimentos importantes que estão transformando a vida da nossa população.',
  excerpt: 'Palmas segue no caminho certo, com trabalho, união e resultados.',
  originalUrl: undefined as string | undefined,
  source: suppliedSource,
};

const galleryRecords: GalleryItem[] = [
  { id: 'asfalto', category: 'Infraestrutura', title: 'Pavimentação urbana', media: projects[0].media[0] },
  { id: 'centro-eventos', category: 'Grandes obras', title: 'Centro de Eventos Maria José Piana', media: projects.find((project) => project.slug === 'centro-eventos')!.media[0] },
  { id: 'retroescavadeiras', category: 'Equipamentos', title: 'Retroescavadeiras', media: projects.find((project) => project.slug === 'maquinas-e-equipamentos')!.media[0] },
  { id: 'eolico', category: 'Investimento privado', title: 'Complexo Eólico Palmas II', media: projects.find((project) => project.slug === 'eolico')!.media[0] },
  { id: 'palmas', category: 'Palmas', title: 'Portal de entrada de Palmas', media: projects.find((project) => project.slug === 'portais-de-entrada')!.media[0] },
  { id: 'terminal', category: 'Grandes obras', title: 'Terminal Rodoviário', media: projects.find((project) => project.slug === 'terminal')!.media[0] },
];

export const gallery: GalleryItem[] = galleryRecords.map((item) => ({
  ...item,
  media: { ...item.media, title: item.title, category: item.category, caption: item.media.caption || item.title },
}));

export const areas = [
  { slug: 'saude', label: 'Saúde', title: 'Estrutura para cuidar das pessoas.', href: '/saude', projectCount: 2 },
  { slug: 'infraestrutura', label: 'Infraestrutura', title: 'Mobilidade para a cidade e o interior.', href: '/infraestrutura', projectCount: 4 },
  { slug: 'grandes-obras', label: 'Grandes obras', title: 'Estrutura que prepara novos caminhos.', href: '/grandes-obras', projectCount: 3 },
] as const;

export const profilePhoto = media('Traiano e Daniel Prefeito.png', 'traiano-daniel', [800, 1280, 2560], [3597, 3520], 'Ademar Traiano e Daniel Langaro');
